import React, { useEffect, useMemo } from 'react';
import { useGetAllKpiLogsQuery } from '@/services/KpiService';
import { DataGrid } from '@/components';
import { KpiLogVm } from '@/types/kpi.types';
import usePagination from '@/hooks/usePagination';
import { formatDisplayDate } from '@/utils/Date';
import { formatFinnishCoordinate, formatFinnishNumber } from '@/utils';

interface KpiLogsTableProps {
  filters?: {
    startDate?: Date;
    endDate?: Date;
    kpiType?: string;
    role?: string;
  };
}

const KpiLogsTable: React.FC<KpiLogsTableProps> = ({ filters = {} }) => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    pagination,
    setPagination,
  } = usePagination();

  const { data, isLoading, error, isFetching } = useGetAllKpiLogsQuery(
    {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      ...filters,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: false,
    }
  );

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [filters, setPagination]);

  useEffect(() => {
    if (data) {
      handleTotalItems(data.totalItems);
      handleTotalPages(data.totalPages);
    }
  }, [data, handleTotalItems, handleTotalPages]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'kpiType',
        header: 'KPI Type',
      },
      {
        accessorKey: 'eventType',
        header: 'Event Type',
        meta: {
          className: 'min-w-[150px]',
        },
      },
      {
        accessorKey: 'metadata.role',
        header: 'Role',
        cell: ({ row }: { row: { original: KpiLogVm } }) => (
          <div>{row.original.metadata?.role || '-'}</div>
        ),
      },
      {
        accessorKey: 'metadata.location_name',
        header: 'Location Name',
        cell: ({ row }: { row: { original: KpiLogVm } }) => (
          <div>{row.original.metadata?.location_name || '-'}</div>
        ),
        meta: {
          className: 'min-w-[200px]',
        },
      },
      {
        accessorKey: 'metadata.latitude',
        header: 'Latitude',
        cell: ({ row }: { row: { original: KpiLogVm } }) => (
          <div>
            {row.original.metadata?.latitude !== undefined &&
            row.original.metadata?.latitude !== null
              ? formatFinnishCoordinate(row.original.metadata.latitude)
              : '-'}
          </div>
        ),
        meta: {
          className: 'min-w-[80px]',
        },
      },
      {
        accessorKey: 'metadata.longitude',
        header: 'Longitude',
        cell: ({ row }: { row: { original: KpiLogVm } }) => (
          <div>
            {row.original.metadata?.longitude !== undefined &&
            row.original.metadata?.longitude !== null
              ? formatFinnishCoordinate(row.original.metadata.longitude)
              : '-'}
          </div>
        ),
      },
      {
        accessorKey: 'metadata.dwell_time',
        header: 'Dwell Time (s)',
        cell: ({ row }: { row: { original: KpiLogVm } }) => (
          <div>
            {formatFinnishNumber(row.original.metadata?.dwell_time ?? '-')}
          </div>
        ),
      },
      {
        accessorKey: 'metadata.booking_status',
        header: 'Booking Status',
        cell: ({ row }: { row: { original: KpiLogVm } }) => (
          <div>{row.original.metadata?.booking_status || '-'}</div>
        ),
      },
      {
        accessorKey: 'timestamp',
        header: 'Timestamp',
        cell: ({ row }: { row: { original: KpiLogVm } }) => (
          <div>{formatDisplayDate(row.original.timestamp)}</div>
        ),
        meta: {
          className: 'min-w-[120px]',
        },
      },
      {
        accessorKey: 'metadata.cancellation_reason',
        header: 'Cancellation Reason',
        cell: ({ row }: { row: { original: KpiLogVm } }) => (
          <div>{row.original.metadata?.cancellation_reason || '-'}</div>
        ),
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32 mt-6">
        <div
          data-testid="loading-spinner"
          className="spinner spinner-primary spinner-lg"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4 text-red-500 mt-6">
        Failed to load KPI logs.
      </div>
    );
  }

  return (
    <div className="mt-8">
      <h2 className="text-xl font-bold text-gray-800 mb-4">KPI Logs</h2>
      {isLoading || isFetching ? (
        <div className="text-center py-4">Loading Logs...</div>
      ) : (
        <DataGrid
          key={`kpi-logs-${pagination.pageIndex}-${pagination.pageSize}-${data?.data?.length || 0}`}
          columns={columns}
          data={data?.data || []}
          rowSelect={false}
          loading={isLoading || isFetching}
          pagination={{
            page: pagination.pageIndex,
            size: pagination.pageSize,
            more: pagination.pageIndex < totalPages - 1,
            totalItems: totalItems,
            totalPages: totalPages,
            setPagination,
            info: `{from} - {to} of ${totalItems}`,
            sizes: [5, 10, 25, 50, 100],
            sizesLabel: 'Show',
            sizesDescription: 'per page',
          }}
          sorting={[{ id: 'timestamp', desc: true }]}
          serverSide={true}
        />
      )}
    </div>
  );
};

export { KpiLogsTable };
